<template>
  <AdminLayout>
    <v-container fluid class="pa-6">
      <v-card elevation="3" class="rounded-lg">
        <v-card-title class="d-flex align-center justify-space-between pa-6 bg-gradient-primary">
          <div class="d-flex align-center">
            <v-icon icon="mdi-gift" class="mr-3" size="32" color="white" />
            <span class="text-h5 font-weight-bold text-white">Rewards Management</span>
          </div>
          <v-btn
            color="white"
            variant="elevated"
            prepend-icon="mdi-plus"
            size="large"
            class="px-6"
            @click="openStoreItemModal"
          >
            Add Reward
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <div v-if="storeItems.length > 0">
            <v-row>
              <v-col cols="12">
                <div class="d-flex align-center mb-4">
                  <v-icon icon="mdi-information" color="info" class="mr-2" />
                  <span class="text-body-1 text-medium-emphasis">
                    {{ storeItems.length }} reward{{ storeItems.length !== 1 ? 's' : '' }} available
                  </span>
                </div>
              </v-col>
            </v-row>
            
            <v-row>
              <v-col 
                v-for="item in storeItems" 
                :key="item.id" 
                cols="12" 
                md="6" 
                lg="4"
              >
                <v-card 
                  elevation="2" 
                  class="h-100 reward-card"
                  :class="{ 'out-of-stock': item.quantity === 0 }"
                >
                  <div class="position-relative">
                    <v-img
                      :src="item.imageUrl"
                      :alt="item.name"
                      height="200"
                      cover
                      class="rounded-t-lg"
                    >
                      <template v-slot:placeholder>
                        <div class="d-flex align-center justify-center fill-height">
                          <v-icon icon="mdi-image" size="48" color="grey-lighten-1" />
                        </div>
                      </template>
                    </v-img>
                    
                    <!-- Out of Stock Badge -->
                    <v-chip
                      v-if="item.quantity === 0"
                      color="error"
                      variant="flat"
                      size="small"
                      class="position-absolute top-0 right-0 ma-2"
                    >
                      Out of Stock
                    </v-chip>
                    
                    <!-- Quantity Badge -->
                    <v-chip
                      :color="item.quantity > 10 ? 'success' : item.quantity > 5 ? 'warning' : 'error'"
                      variant="flat"
                      size="small"
                      class="position-absolute top-0 left-0 ma-2"
                    >
                      Qty: {{ item.quantity }}
                    </v-chip>
                  </div>
                  
                  <v-card-text class="pa-4">
                    <h3 class="text-h6 font-weight-bold mb-2">{{ item.name }}</h3>
                    <p class="text-body-2 text-medium-emphasis mb-3 line-clamp-2">
                      {{ item.description }}
                    </p>
                    
                    <div class="d-flex align-center justify-space-between">
                      <v-chip
                        color="primary"
                        variant="elevated"
                        size="large"
                        class="font-weight-bold"
                      >
                        <v-icon icon="mdi-currency-usd" class="mr-1" />
                        {{ item.points }} Tokens
                      </v-chip>
                    </div>
                  </v-card-text>
                  
                  <v-card-actions class="pa-4 pt-0">
                    <v-btn
                      prepend-icon="mdi-pencil"
                      variant="outlined"
                      size="default"
                      color="primary"
                      class="mr-2 action-btn"
                      @click="editStoreItem(item)"
                    >
                      Edit
                      <v-tooltip activator="parent" location="top">Edit Reward</v-tooltip>
                    </v-btn>
                    <v-btn
                      prepend-icon="mdi-delete"
                      variant="outlined"
                      size="default"
                      color="error"
                      class="action-btn"
                      @click="deleteStoreItem(item.id)"
                    >
                      Delete
                      <v-tooltip activator="parent" location="top">Delete Reward</v-tooltip>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-chip
                      :color="item.quantity > 10 ? 'success' : item.quantity > 5 ? 'warning' : 'error'"
                      variant="flat"
                      size="small"
                    >
                      <v-icon 
                        :icon="item.quantity > 10 ? 'mdi-check-circle' : item.quantity > 5 ? 'mdi-alert-circle' : 'mdi-close-circle'" 
                        class="mr-1" 
                      />
                      {{ item.quantity > 10 ? 'In Stock' : item.quantity > 5 ? 'Low Stock' : 'Critical' }}
                    </v-chip>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </div>
          
          <div v-else class="text-center pa-12">
            <v-icon icon="mdi-gift-off" size="80" color="grey-lighten-1" class="mb-4" />
            <h3 class="text-h5 text-grey-darken-1 mb-2">No Rewards Available</h3>
            <p class="text-body-1 text-medium-emphasis mb-6">
              Get started by adding your first reward to the store
            </p>
            <v-btn
              color="primary"
              variant="elevated"
              prepend-icon="mdi-plus"
              size="large"
              @click="openStoreItemModal"
            >
              Add Your First Reward
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Store Item Modal -->
    <v-dialog
      v-model="showStoreItemModal"
      max-width="600px"
      persistent
    >
      <v-card class="rounded-lg">
        <v-card-title class="d-flex align-center pa-6 bg-gradient-primary text-white">
          <v-icon icon="mdi-gift" class="mr-3" />
          <span class="text-h5 font-weight-bold">
            {{ editingItem ? 'Edit' : 'Create' }} Reward
          </span>
        </v-card-title>
        
        <v-card-text class="pa-6">
          <v-form @submit.prevent="saveStoreItem">
            <v-container fluid class="pa-0">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="storeItemForm.name"
                    label="Reward Name"
                    required
                    variant="outlined"
                    density="comfortable"
                    :error-messages="formErrors.name"
                    :error="!!formErrors.name"
                    prepend-inner-icon="mdi-tag"
                    placeholder="Enter reward name"
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="storeItemForm.description"
                    label="Description"
                    variant="outlined"
                    density="comfortable"
                    rows="3"
                    prepend-inner-icon="mdi-text"
                    placeholder="Describe what this reward offers"
                  ></v-textarea>
                </v-col>

                <v-col cols="6">
                  <v-text-field
                    v-model.number="storeItemForm.points"
                    label="Token Cost"
                    type="number"
                    required
                    min="1"
                    variant="outlined"
                    density="comfortable"
                    :error-messages="formErrors.points"
                    :error="!!formErrors.points"
                    prepend-inner-icon="mdi-currency-usd"
                    placeholder="0"
                  ></v-text-field>
                </v-col>

                <v-col cols="6">
                  <v-text-field
                    v-model.number="storeItemForm.quantity"
                    label="Available Quantity"
                    type="number"
                    required
                    min="0"
                    variant="outlined"
                    density="comfortable"
                    :error-messages="formErrors.quantity"
                    :error="!!formErrors.quantity"
                    prepend-inner-icon="mdi-package-variant"
                    placeholder="0"
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-file-input
                    v-model="imageFile"
                    label="Reward Image"
                    accept="image/*"
                    variant="outlined"
                    density="comfortable"
                    prepend-icon="mdi-camera"
                    :show-size="true"
                    :rules="[v => !editingItem || v || imagePreview ? true : 'Image is required']"
                    @change="handleImageChange"
                    placeholder="Select an image for the reward"
                  >
                    <template v-slot:selection="{ fileNames }">
                      <template v-for="fileName in fileNames" :key="fileName">
                        <v-chip
                          size="small"
                          label
                          color="primary"
                          class="me-2"
                        >
                          {{ fileName }}
                        </v-chip>
                      </template>
                    </template>
                  </v-file-input>
                  
                  <div v-if="imagePreview" class="mt-3">
                    <div class="d-flex align-center mb-2">
                      <v-icon icon="mdi-eye" class="mr-2" color="primary" />
                      <span class="text-body-2 font-weight-medium">Image Preview</span>
                    </div>
                    <div class="position-relative d-inline-block">
                      <img :src="imagePreview" alt="Preview" class="preview-image" />
                      <v-btn
                        icon="mdi-close"
                        size="small"
                        color="error"
                        class="position-absolute top-0 right-0 ma-1"
                        @click="clearImage"
                      ></v-btn>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            size="large"
            @click="closeStoreItemModal"
            class="px-6"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            size="large"
            @click="saveStoreItem"
            :loading="saving"
            class="px-6"
            prepend-icon="mdi-content-save"
          >
            {{ editingItem ? 'Save Changes' : 'Create Reward' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import AdminLayout from './components/AdminLayout.vue'
import { storage } from './firebase'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { useAuthStore } from './stores/authStore'

const router = useRouter()
const saving = ref(false)
const storeItems = ref([])
const showStoreItemModal = ref(false)
const editingItem = ref(null)
const imageFile = ref(null)
const imagePreview = ref('')

const storeItemForm = ref({
  name: '',
  description: '',
  points: 0,
  quantity: 0,
  imageUrl: ''
})

const formErrors = ref({
  name: '',
  points: '',
  quantity: ''
})

const validateForm = () => {
  formErrors.value = {
    name: '',
    points: '',
    quantity: ''
  }
  
  let isValid = true
  
  if (!storeItemForm.value.name.trim()) {
    formErrors.value.name = 'Name is required'
    isValid = false
  }
  
  if (!storeItemForm.value.points || storeItemForm.value.points <= 0) {
    formErrors.value.points = 'Tokens must be greater than 0'
    isValid = false
  }
  
  if (storeItemForm.value.quantity === null || storeItemForm.value.quantity === undefined || storeItemForm.value.quantity < 0) {
    formErrors.value.quantity = 'Quantity must be 0 or greater'
    isValid = false
  }
  
  return isValid
}

const handleImageChange = (file) => {
  if (!file) {
    imagePreview.value = ''
    return
  }
  
  const reader = new FileReader()
  reader.onload = (e) => {
    imagePreview.value = e.target.result
  }
  reader.readAsDataURL(file)
}

const uploadImage = async (file: File): Promise<string | null> => {
  if (!file) return null
  
  try {
    // Create a unique filename
    const timestamp = Date.now()
    const filename = `store-items/${timestamp}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
    
    // Create a reference to the file location in Firebase Storage
    const fileRef = storageRef(storage, filename)
    
    // Upload the file
    const snapshot = await uploadBytes(fileRef, file)
    
    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref)
    return downloadURL
  } catch (error) {
    console.error('Error uploading image:', error)
    throw error
  }
}

const saveStoreItem = async () => {
  if (!validateForm()) {
    alert('Please fill in all required fields correctly.')
    return
  }

  if (!editingItem.value && !imageFile.value && !imagePreview.value) {
    alert('Please select an image')
    return
  }

  saving.value = true
  
  try {
    let imageUrl = storeItemForm.value.imageUrl
    
    if (imageFile.value) {
      imageUrl = await uploadImage(imageFile.value)
    }
    
    const payload = {
      ...storeItemForm.value,
      imageUrl
    }
    
    const url = editingItem.value
      ? `${process.env.VUE_APP_API_BASE_URL}/store/items/${editingItem.value.id}`
      : `${process.env.VUE_APP_API_BASE_URL}/store/items`
      
    const method = editingItem.value ? 'PUT' : 'POST'
    
    const response = await fetch(url, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    
    if (!response.ok) {
      throw new Error('Failed to save store item')
    }
    
    await fetchStoreItems()
    closeStoreItemModal()
  } catch (error) {
    console.error('Error saving store item:', error)
    alert('Failed to save store item: ' + error.message)
  
    saving.value = false
  }
}

const closeStoreItemModal = () => {
  showStoreItemModal.value = false
  editingItem.value = null
  storeItemForm.value = {
    name: '',
    description: '',
    points: 0,
    quantity: 0,
    imageUrl: ''
  }
  formErrors.value = {
    name: '',
    points: '',
    quantity: ''
  }
  imageFile.value = null
  imagePreview.value = ''
  saving.value = false
}

const clearImage = () => {
  imageFile.value = null
  imagePreview.value = ''
  storeItemForm.value.imageUrl = ''
}

const editStoreItem = (item) => {
  editingItem.value = item
  storeItemForm.value = { 
    name: item.name,
    description: item.description,
    points: item.points,
    quantity: item.quantity,
    imageUrl: item.imageUrl
  }
  formErrors.value = {
    name: '',
    points: '',
    quantity: ''
  }
  if (item.imageUrl) {
    imagePreview.value = item.imageUrl
  }
  showStoreItemModal.value = true
}

const deleteStoreItem = async (itemId) => {
  if (!confirm('Are you sure you want to delete this item?')) return

  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/store/items/${itemId}`, {
      method: 'DELETE',
      credentials: 'include'
    })

    if (response.ok) {
      await fetchStoreItems()
    }
  } catch (error) {
    console.error('Error deleting store item:', error)
  }
}

const fetchStoreItems = async () => {
  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/store/items`, {
      credentials: 'include'
    })
    if (response.ok) {
      storeItems.value = await response.json()
    }
  } catch (error) {
    console.error('Error fetching store items:', error)
  }
}

function openStoreItemModal() {
  editingItem.value = null
  storeItemForm.value = {
    name: '',
    description: '',
    points: 0,
    quantity: 0,
    imageUrl: ''
  }
  formErrors.value = {
    name: '',
    points: '',
    quantity: ''
  }
  showStoreItemModal.value = true
}

const { checkAuth } = useAuthStore()

onMounted(async () => {
  try {
    // Check if user is admin using auth store
    const authData = await checkAuth()
    if (!authData.isAdmin) {
      router.push('/dashboard')
      return
    }

    await fetchStoreItems()
  } catch (error) {
    console.error('Error loading admin rewards:', error)
  }
})
</script>

<style scoped>
.action-btn {
  min-width: 80px;
  height: 36px;
  transition: all 0.2s ease;
  font-weight: 500;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.action-btn.v-btn--variant-outlined {
  border-width: 2px;
}

.action-btn.v-btn--variant-outlined:hover {
  border-width: 2px;
}

.preview-image {
  max-width: 200px;
  max-height: 150px;
  object-fit: contain;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
}

.reward-card {
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
}

.reward-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: #1976d2;
}

.reward-card.out-of-stock {
  opacity: 0.7;
  filter: grayscale(0.3);
}

.reward-card.out-of-stock:hover {
  transform: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

.position-relative {
  position: relative;
}

.position-absolute {
  position: absolute;
}

.top-0 {
  top: 0;
}

.right-0 {
  right: 0;
}

.left-0 {
  left: 0;
}

.ma-1 {
  margin: 4px;
}

.ma-2 {
  margin: 8px;
}

.mb-2 {
  margin-bottom: 8px;
}

.mb-3 {
  margin-bottom: 12px;
}

.mb-4 {
  margin-bottom: 16px;
}

.mb-6 {
  margin-bottom: 24px;
}

.mr-1 {
  margin-right: 4px;
}

.mr-2 {
  margin-right: 8px;
}

.mr-3 {
  margin-right: 12px;
}

.me-2 {
  margin-right: 8px;
}

.mt-2 {
  margin-top: 8px;
}

.mt-3 {
  margin-top: 12px;
}

.pa-0 {
  padding: 0;
}

.pa-4 {
  padding: 16px;
}

.pa-6 {
  padding: 24px;
}

.pa-12 {
  padding: 48px;
}

.pt-0 {
  padding-top: 0;
}

.px-6 {
  padding-left: 24px;
  padding-right: 24px;
}

.h-100 {
  height: 100%;
}

.d-inline-block {
  display: inline-block;
}

.rounded-lg {
  border-radius: 12px;
}

.rounded-t-lg {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.text-white {
  color: white !important;
}

.text-grey-darken-1 {
  color: #424242 !important;
}

.text-medium-emphasis {
  color: rgba(0, 0, 0, 0.6) !important;
}

.font-weight-bold {
  font-weight: 700 !important;
}

.font-weight-medium {
  font-weight: 500 !important;
}

.text-h5 {
  font-size: 1.5rem !important;
  line-height: 2rem !important;
}

.text-h6 {
  font-size: 1.25rem !important;
  line-height: 1.75rem !important;
}

.text-body-1 {
  font-size: 1rem !important;
  line-height: 1.5rem !important;
}

.text-body-2 {
  font-size: 0.875rem !important;
  line-height: 1.25rem !important;
}
</style> 